import { FC, useState, useEffect, useRef, useMemo } from 'react'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'
import { isEqual } from 'lodash'
import {
  useMultiStyleConfig,
  Center,
  HStack,
  VStack,
  Flex,
  Spinner,
  Box,
  Image,
  Text,
} from '@chakra-ui/react'
import Webcam from 'react-webcam'
import UIFx from 'uifx'

import {
  GameInfo,
  GameState,
  GameStatus,
  GameContainerDimensions,
} from '../types/game'
import useLocalStorage from '../hooks/use-local-storage'
import { Timer, Score, LeaderBoard, GameSelector } from '../components'
import { BalloonGame } from '../game'
import RewardBg from '../assets/reward.svg'
import InstructionBg from '../assets/Instructions.svg'
// Sound effect retrieved from https://mixkit.co/
import CountdownSfx from '../assets/end.wav'
// Sound effect retrieved from http://www.orangefreesounds.com/
import EndSfx from '../assets/game-end.mp3'

const COUNTDOWN_SFX_SRC = CountdownSfx
const END_SFX_SRC = EndSfx
const exampleGame = {
  name: 'Handsy',
  duration: 30,
  topScores: [1000, 900, 200],
}

const defaultSettings = {
  detectionInterval: 500,
  movingTargets: false,
  minIou: 0.05,
  minRelativeHandArea: 0.02, // 1/16 of screen
  maxRelativeHandArea: 0.25, // 1/8 of screen,
  relativeDrop: 0.008,
  numBalloons: 1,
  pointsPerBalloon: 50,
  duration: 30,
  targetDelay: 3,
}

export const Main: FC = () => {
  const styles = useMultiStyleConfig('Main', {})
  const { search } = useLocation()
  const settings = useMemo(() => {
    return {
      ...defaultSettings,
      ...queryString.parse(search, { parseBooleans: true }),
    }
  }, [search])

  const webcam = useRef<Webcam | null>(null)
  const overlay = useRef<HTMLCanvasElement | null>(null)
  const game = useRef<BalloonGame | null>(null)
  const gameContainer = useRef<HTMLDivElement | null>(null)

  const [highScores, setHighScores] = useLocalStorage<number[]>(
    `${exampleGame.name}-highscores`,
    []
  )
  const [gameState, setGameState] = useState<GameState>({
    instructions: 'Loading...',
    status: GameStatus.Loading,
    score: 0,
  })
  const [timeLeft, setTimeLeft] = useState(settings.duration)
  const [dimensions, setDimensions] = useState<GameContainerDimensions | null>(
    null
  )
  const [countdownSfx] = useState(
    new UIFx(COUNTDOWN_SFX_SRC, {
      volume: 0.4, // number between 0.0 ~ 1.0
      throttleMs: 100,
    })
  )
  const [endSfx] = useState(
    new UIFx(END_SFX_SRC, {
      volume: 0.4, // number between 0.0 ~ 1.0
      throttleMs: 100,
    })
  )

  useEffect(() => {
    setDimensions({
      width: gameContainer.current!.offsetWidth,
      height: gameContainer.current!.offsetHeight,
    })
  }, [])

  useEffect(() => {
    if (webcam.current?.video?.readyState === 4) {
      setDimensions({
        width: webcam.current!.video!.videoWidth,
        height: webcam.current!.video!.videoHeight,
      })
    }
  }, [webcam.current?.video?.readyState])

  useEffect(() => {
    const initialize = async () => {
      if (dimensions) {
        game.current = await BalloonGame.create(dimensions, settings)
      }
    }
    initialize()
  }, [game, dimensions, settings])

  useEffect(() => {
    // Start timer only when game is transitioning to Playing state
    if (
      gameState.prevStatus === GameStatus.Calibrating &&
      gameState.status === GameStatus.Playing
    ) {
      const interval = setInterval(() => {
        setTimeLeft((t) => {
          const tt = Math.max(t - 1, 0)
          if (tt === 0) {
            setGameState((gs) => {
              setHighScores(
                [...highScores, gs.score].sort().reverse().slice(0, 3)
              )
              setTimeout(() => endSfx.play(), 500)
              return {
                ...gs,
                instructions: 'Game ended',
                status: GameStatus.Finished,
              }
            })

            clearInterval(interval)
          }

          return tt
        })
      }, 1000)
    }
  }, [gameState, timeLeft, highScores, setHighScores])

  useEffect(() => {
    const interval = setInterval(async () => {
      if (game.current && webcam.current && overlay.current) {
        // Evaluate new game state and paint new canvas
        const newState = await game.current.update(
          webcam.current,
          overlay.current,
          gameState
        )
        // Update game state if changed in instruction or score
        if (!isEqual(gameState, newState)) {
          setGameState(newState)
        }
      }
    }, settings.detectionInterval)

    return () => clearInterval(interval)
  }, [game, webcam, gameState, settings])

  useEffect(() => {
    if (timeLeft === 3) {
      countdownSfx.play()
    }
  }, [timeLeft])

  const startGame = () => {
    setGameState((gs) => ({
      ...gs,
      status: GameStatus.Start,
      prevStatus: gs.status,
    }))
  }

  const restartGame = () => {
    setGameState((gs) => ({
      ...gs,
      status: GameStatus.Start,
      prevStatus: gs.status,
      score: 0,
    }))
    setTimeLeft(settings.duration)
  }

  return (
    <HStack spacing={0} align="stretch" h="100vh" fontFamily="Fredoka One">
      <Flex flex={1} flexDir="column">
        <Center flex={1} position="relative">
          <Center sx={styles.logo} zIndex={3}>
            Handsy
          </Center>
          {gameState.status === GameStatus.Loading && (
            <Center sx={styles.wrapper} bg="rgba(0,0,0,0.8)" zIndex={2}>
              <Spinner color="white" size="xl" thickness="4px" speed="0.7s" />
            </Center>
          )}
          {gameState.status === GameStatus.Calibrating && (
            <Center
              sx={styles.wrapper}
              bg="rgba(0,0,0,0.8)"
              zIndex={2}
              cursor="pointer"
            >
              <Text
                color="white"
                textAlign="center"
                position="absolute"
                w="100%"
                top="60%"
                fontSize="24px"
              >
                Raise up one hand!
                <br />
                We're calibrating...
              </Text>
              <Spinner
                color="white"
                size="xl"
                thickness="4px"
                speed="0.7s"
                position="absolute"
                top="70%"
              />
              <Image boxSize="500px" src={InstructionBg} />
            </Center>
          )}
          {gameState.status === GameStatus.Ready && (
            <Center
              sx={styles.wrapper}
              bg="rgba(0,0,0,0.8)"
              zIndex={2}
              cursor="pointer"
            >
              <Box position="relative" onClick={startGame}>
                <Text
                  textAlign="center"
                  position="absolute"
                  w="100%"
                  top="128px"
                  fontSize="24px"
                >
                  Catch the gold coins! <br />
                  Click here to start
                </Text>
                <Image boxSize="500px" src={RewardBg} />
              </Box>
            </Center>
          )}
          {gameState.status === GameStatus.Finished && (
            <Center sx={styles.wrapper} bg="rgba(0,0,0,0.8)" zIndex={2}>
              <Box position="relative" cursor="pointer" onClick={restartGame}>
                <Text
                  textAlign="center"
                  position="absolute"
                  w="100%"
                  top="124px"
                  fontSize="24px"
                >
                  You scored {gameState.score} points.
                  <br />
                  Click to play again
                  <br />
                </Text>
                <Image boxSize="500px" src={RewardBg} />
              </Box>
            </Center>
          )}
          <Center sx={styles.wrapper} bg="black" zIndex={1} ref={gameContainer}>
            {dimensions && (
              <>
                <Webcam
                  ref={webcam}
                  mirrored
                  videoConstraints={{
                    facingMode: 'user',
                    width: dimensions!.width,
                    height: dimensions!.height,
                  }}
                  audio={false}
                  style={{
                    position: 'absolute',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                />
                <canvas
                  ref={overlay}
                  width={dimensions!.width}
                  height={dimensions!.height}
                  style={{
                    position: 'absolute',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                  }}
                />
              </>
            )}
          </Center>
        </Center>
        <Center h={24} bg="#494C4F" color="white" fontSize="48px">
          {gameState.instructions}
        </Center>
      </Flex>
      <VStack sx={styles.sidebar} spacing={16} align="stretch">
        <Timer timeLeft={timeLeft} />
        <Score score={gameState.score} />
        <LeaderBoard scores={highScores} />
        <GameSelector />
      </VStack>
    </HStack>
  )
}
